import { Component, Inject, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { IIntervalService, IRootScopeService, IScope } from 'angular';
import { filter } from 'lodash';
import { combineLatest, Observable, Subject, Subscription, timer } from 'rxjs';
import { debounce, skip, tap } from 'rxjs/operators';
import { unsubscribeComponent } from '../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { DateHelpers } from '../shared/services/date-helpers/date-helpers.service';
import { ImStudent } from '../shared/services/im-models/im-student.service';
import { WindowRef } from '../shared/services/windowRef';
import { IFlag } from '../shared/typings/interfaces/flags.interface';
import { ISchool } from '../shared/typings/interfaces/school.interface';
import { IStudentAssessment } from '../shared/typings/interfaces/student-assessment.interface';
import { IStudentSupport } from '../shared/typings/interfaces/student-support.interface';
import { IUser } from '../shared/typings/interfaces/user.interface';
import { UpdateDashboardStudentsSuccess } from '../store';
import { LoadStudentMilestones } from '../store/actions/milestone-actions';
import { UpdatePatchProcessing } from '../store/actions/patch-processing-actions';
import { UpdateSingleStudent } from '../store/actions/single-student-actions';
import { LoadStudentPath } from '../store/actions/student-paths-actions';
import { IActivity } from './../shared/typings/interfaces/activity.interface';
import { IPartnerOrg } from './../shared/typings/interfaces/partner-org.interface';
import { IStudentActivity } from './../shared/typings/interfaces/student-activity.interface';
import {
  BulkUpdateAcademicStudentsSuccess,
  BulkUpdateAttendanceStudentsSuccess,
  BulkUpdateMyStudentsSuccess,
  BulkUpdatePostsecondaryStudentsSuccess,
} from './../store/actions/students-actions';

export interface IStudentUpdate {
  patch;
  path: string;
}

@Component({
  selector: 'student',
  template: require('./student.component.html'),
})
@unsubscribeComponent
export class StudentComponent implements OnInit {
  @Input() studentList$: Observable<any>;
  @Input() patchProcessing$: Observable<any>;
  @Input() currentStudent$: Observable<any>;
  @Input() currentStudentId: string;
  @Input() currentUser: IUser;
  @Input() channel;
  @Input() currentSchool: ISchool;
  @Input() studentDiffs;
  @Input() filteredStudents;
  @Input() schoolUsers;
  @Input() students;
  @Input() studentSupports$: Observable<IStudentSupport[]>;
  @Input() supports;
  @Input() parsedFilterParam;
  @Input() parsedSortParam;
  @Input() studentGapPlans;
  @Input() flags$: Observable<IFlag[]>;
  @Input() docLogs$: any; // using Observable<IDocLog[]> is failing build
  @Input() ssvExitState: string;
  @Input() toggleVersion: boolean;
  @Input() milestones$: Observable<any>;
  @Input() studentPaths$: Observable<any>;
  @Input() collegePaths$: Observable<any>;
  @Input() careerPaths$: Observable<any>;
  @Input() activities$: Observable<IActivity[]>;
  @Input() studentActivities$: Observable<IStudentActivity[]>;
  @Input() studentAssessments$: Observable<IStudentAssessment[]>;
  @Input() partnerOrgs$: Observable<IPartnerOrg[]>;
  @Input() isSummerSchoolStudent: boolean;

  public savedTimestamp;
  public currentState;
  public flags;
  public studentSupports;
  public currentStudent;
  public studentList;
  public isRestricted;
  public patchProcessing;
  public _milestoneCount;
  private toastSub: Subscription;
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<any>,
    private windowRef: WindowRef,
    private dateHelpers: DateHelpers,
    @Inject('$mdToast') private $mdToast,
    @Inject('$rootScope') private $rootScope: IRootScopeService,
    @Inject('StudentSupport') private StudentSupport,
    @Inject('$scope') private $scope: IScope,
    @Inject('$interval') private $interval: IIntervalService,
    @Inject('ErrorModal') private ErrorModal,
    @Inject('Rollbar') private Rollbar,
    @Inject('SchoolToolbarService') private SchoolToolbarService,
    private imStudent: ImStudent,
    @Inject('Student') private Student,
    @Inject('SpinnerModal') private spinnerModal,
  ) {
    //
  }

  public ngOnInit(): void {
    this._milestoneCount = 17;

    this.addListenerForOnExit();

    this.mergeObservables();
    this.subscribeToObservables();

    // DO NOT REMOVE
    this.isRestricted = !this.imStudent.canEdit(this.currentStudent, this.currentUser, this.currentSchool._id);

    // current state
    this.currentState = this.SchoolToolbarService.getCurrentState();

    this.spinnerModal.deactivateSpinner();
  }

  mergeObservables(): void {
    const combinedObservable = combineLatest(
      this.milestones$,
      this.currentStudent$,
      this.flags$,
      this.studentSupports$,
    );
    this.toastSub = combinedObservable
      .pipe(
        skip(1),
        debounce(() => timer(1500)),
        tap(() => {
          this.showToast();
        }),
      )
      .subscribe();
  }

  subscribeToObservables(): void {
    this.patchProcessing$
      .pipe(
        tap(({ patchProcessing }) => {
          this.patchProcessing = patchProcessing;
        }),
      )
      .subscribe();

    this.currentStudent$
      .pipe(
        tap(currentStudent => {
          this.currentStudent = currentStudent;
        }),
      )
      .subscribe();

    if (this.studentSupports$) {
      this.studentSupports$
        .pipe(
          tap(studentSupports => {
            this.studentSupports = this.setStudentSupports(studentSupports);
          }),
        )
        .subscribe();
    }

    if (this.flags$) {
      this.flags$
        .pipe(
          tap(flags => {
            this.flags = this.setFlags(flags);
          }),
        )
        .subscribe();
    }
  }

  public showToast(): void {
    let toastScope: any = this.$rootScope.$new();
    toastScope.message = 'This student was updated.';
    toastScope.mdToastAction = null;
    this.$mdToast
      .show({
        hideDelay: 2000,
        position: 'bottom left',
        template: require('../../app/student/stateless-components/stale-student-toast/stale-student-toast.html'),
        scope: toastScope,
      })
      .catch(err => {
        if (err) this.Rollbar.warning(err);
        this.Rollbar.warning('Failed to show toast');
      });
  }

  addListenerForOnExit(): void {
    this.$scope.$on('$stateChangeStart', event => this.checkUnsavedChanges(event));
    this.windowRef.nativeWindow.addEventListener('beforeunload', e => {
      if (this.patchProcessing) e.returnValue = true;
    });
  }

  public checkUnsavedChanges(event): void {
    if (this.patchProcessing) {
      let answer = this.windowRef.nativeWindow.confirm(
        'You have unsaved changes, Are you sure you want to leave this page?',
      );
      if (!answer) {
        event.preventDefault();
      }
    }
  }

  setFlags(flags): IFlag[] {
    return filter(flags, (flag: IFlag) => {
      return flag.studentId === this.currentStudentId;
    });
  }

  setStudentSupports(studentSupports): IStudentSupport[] {
    return filter(studentSupports, (studentSupport: IStudentSupport) => {
      return studentSupport.student.studentId === this.currentStudentId;
    });
  }

  public updateSavedTimestamp(): void {
    this.savedTimestamp = 'Last saved ' + this.dateHelpers.getMomentObj().to(new Date());
  }

  saveStudent(studentPatch: IStudentUpdate) {
    this.store.dispatch(new UpdatePatchProcessing());

    this.updateSavedTimestamp();
    // when grad date is patched, milestone and studentPath due dates get updated
    if (studentPatch.path === 'gradPlanningDetails.plannedGraduationDate') {
      const { schoolId } = this.currentStudent;
      const studentId = this.currentStudent._id;
      this.store.dispatch(new LoadStudentMilestones({ schoolId, studentId }));
      this.store.dispatch(new LoadStudentPath({ schoolId, studentId }));
    }
    const { patch } = studentPatch;
    const id = this.currentStudentId;
    this.store.dispatch(new UpdateSingleStudent({ patch, id }));

    // TODO: replace this logic with a meta-reducer that checks projections on slices of
    // the store and updates them accordingly (JE)
    // Dispatch actions to update all student slices (JE)
    const patches = [{ _ids: [this.currentStudent._id], patch: this.currentStudent }];
    this.store.dispatch(new BulkUpdateAttendanceStudentsSuccess({ patches }));
    this.store.dispatch(new BulkUpdateMyStudentsSuccess({ patches }));
    this.store.dispatch(new BulkUpdateAcademicStudentsSuccess({ patches }));
    this.store.dispatch(new BulkUpdatePostsecondaryStudentsSuccess({ patches }));
    this.store.dispatch(new UpdateDashboardStudentsSuccess({ patches }));
  }
}
